import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ACTION_KEYS, EXTRA_SHORTCUTS, SIDEBAR_GROUPS, matchSidebarKey } from "@/lib/sidebarShortcuts";
import { ShortcutSidebar } from "@/components/ShortcutSidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/AuthContext";
import { FabricImage } from "@/components/FabricImage";
import { CompanyDialog } from "@/components/CompanyDialog";
import { SalesRateDialog } from "@/components/SalesRateDialog";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Camera, ChevronDown, LogOut, Menu } from "lucide-react";
import { MENU_COLORS, themeForPath, themeVars } from "@/lib/moduleTheme";

const MENUS = [
  { label: "Dashboard", testId: "menu-dashboard", to: "/dashboard", perm: "read" },
  {
    label: "Admin",
    testId: "menu-admin",
    perm: "items",
    links: [
      { to: "/items", label: "Item Master", testId: "nav-items", perm: "items" },
      { to: "/quick-add", label: "Quick Add Photo", testId: "nav-quick-add", perm: "items" },
      { to: "/masters/quality", label: "Quality Master", testId: "nav-quality-master", perm: "masters" },
      { to: "/masters/company", label: "Company Master", testId: "nav-company-master", perm: "masters" },
      { to: "/masters/main-unit", label: "Main Unit Master", testId: "nav-main-unit-master", perm: "masters" },
      { to: "/masters/alt-unit", label: "Alternative Unit Master", testId: "nav-alt-unit-master", perm: "masters" },
      { to: "/masters/centre", label: "Material Centre Master", testId: "nav-centre-master", perm: "masters" },
      { to: "/masters/supplier", label: "Supplier Master", testId: "nav-supplier-master", perm: "masters" },
      { to: "/masters/customer", label: "Customer Master", testId: "nav-customer-master", perm: "masters" },
      { to: "/masters/expense-head", label: "Expense Head Master", testId: "nav-expense-head-master", perm: "masters" },
      { action: "company", label: "Company Details", testId: "nav-company-details", perm: "profile" },
      { to: "/users", label: "Staff Logins", testId: "nav-users", perm: "users" },
      { to: "/activity", label: "Staff Activity Log", testId: "nav-activity", perm: "users" },
    ],
  },
  {
    label: "Transactions",
    testId: "menu-transactions",
    links: [
      { to: "/sales", label: "Sales", testId: "nav-sales", perm: "sales" },
      { to: "/sales-orders", label: "Sales Order", testId: "nav-sales-orders", perm: "sales" },
      { to: "/purchase", label: "Purchase", testId: "nav-purchase", perm: "purchase" },
      { to: "/mill-orders/order", label: "Mill Order Sheet", testId: "nav-mill-orders", perm: "purchase" },
      { to: "/mill-orders/pending", label: "Pending Orders", testId: "nav-mill-pending", perm: "purchase" },
      { to: "/mill-orders/dispatch", label: "Dispatch", testId: "nav-mill-dispatch", perm: "purchase" },
      { to: "/purchase-orders", label: "Purchase Order", testId: "nav-purchase-orders", perm: "purchase" },
      { to: "/adjustments", label: "Stock Adjustment", testId: "nav-adjustments", perm: "adjustment" },
      { to: "/transfers", label: "Stock Transfer", testId: "nav-transfers", perm: "adjustment" },
      { to: "/journal", label: "Journal Entry", testId: "nav-journal", perm: "payments" },
      { to: "/payment-entry", label: "Payment Entry", testId: "nav-payment-entry", perm: "payments" },
      { to: "/receipt-entry", label: "Receipt Entry", testId: "nav-receipt-entry", perm: "payments" },
    ],
  },
  {
    label: "Hisab",
    testId: "menu-hisab",
    perm: "hisab",
    links: [
      { to: "/hisab", label: "Customer Hisab", testId: "nav-hisab", perm: "hisab" },
      { to: "/customers", label: "Customer Dashboard", testId: "nav-customers", perm: "hisab" },
    ],
  },
  {
    label: "Display",
    testId: "menu-display",
    links: [
      { to: "/inventory", label: "Inventory", testId: "nav-inventory" },
      { to: "/reorder", label: "Low Stock & Reorder", testId: "nav-reorder" },
      { to: "/scan", label: "Scan Than QR", testId: "nav-scan" },
      { to: "/rate-finder", label: "Rate Finder", testId: "nav-rate-finder", perm: "rates" },
      { to: "/reports", label: "Sales & Purchase Report", testId: "nav-reports" },
      { to: "/profit-report", label: "Profit Report", testId: "nav-profit-report", perm: "costs" },
      { to: "/bale-report", label: "Bale & LR Report", testId: "nav-bale-report" },
      { to: "/purchase-register", label: "Purchase Register", testId: "nav-purchase-register" },
      { to: "/expense-report", label: "Expense Ledger", testId: "nav-expense-report" },
      { to: "/ledger", label: "Item Ledger", testId: "nav-ledger" },
      { to: "/party-ledger", label: "Party Ledger", testId: "nav-party-ledger" },
    ],
  },
  {
    label: "Booking",
    testId: "menu-booking",
    perm: "sales",
    links: [
      { to: "/conference", label: "Booking Dashboard", testId: "nav-booking-dashboard", perm: "sales" },
      { to: "/conference/orders", label: "Conference Order", testId: "nav-booking-orders", perm: "sales" },
      { to: "/conference/summary", label: "Order Summary", testId: "nav-booking-summary", perm: "sales" },
      { to: "/conference/company-order", label: "Company Order", testId: "nav-booking-company-order", perm: "sales" },
      { to: "/conference/conf-company-order", label: "Conference Company Order", testId: "nav-booking-conf-company-order", perm: "sales" },
      { to: "/conference/wholesale-company-order", label: "Wholesale Company Order", testId: "nav-booking-wholesale-company-order", perm: "sales" },
      { to: "/conference/stock", label: "In House Stock", testId: "nav-booking-stock", perm: "sales" },
      { to: "/conference/balance-order", label: "Balance Order", testId: "nav-booking-balance-order", perm: "sales" },
      { to: "/conference/balance", label: "Balance Stock", testId: "nav-booking-balance", perm: "sales" },
      { to: "/conference/extra-stock", label: "Extra Stock", testId: "nav-booking-extra-stock", perm: "sales" },
      { to: "/conference/parties", label: "Booking Parties", testId: "nav-booking-parties", perm: "sales" },
    ],
  },
];

export const AppShell = ({ profile, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [companyOpen, setCompanyOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const allowed = user?.permissions || [];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F1" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }
      if (e.key === "F2" && !e.ctrlKey && !e.metaKey) {
        // F2 saves whatever form is on screen
        const buttons = Array.from(document.querySelectorAll("[data-f2-save]"));
        const target = buttons.reverse().find((b) => b.offsetParent !== null && !b.disabled);
        if (target) {
          e.preventDefault();
          target.click();
          return;
        }
      }
      const hit = matchSidebarKey(e, allowed);
      if (!hit) return;
      e.preventDefault();
      if (hit.action === "sales-rate") {
        setRateOpen(true);
        return;
      }
      navigate(hit.to);
    };
    const onOpenRate = () => setRateOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-sales-rate", onOpenRate);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-sales-rate", onOpenRate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed.join(","), navigate]);
  const menus = MENUS.map((m) => (m.to ? m : { ...m, links: m.links.filter((l) => !l.perm || allowed.includes(l.perm)) }))
    .filter((m) => (m.to ? !m.perm || allowed.includes(m.perm) : m.links.length > 0));

  const theme = themeForPath(pathname);

  return (
    <div className="mod-theme min-h-screen bg-[#FDFDFD] lg:pr-[var(--sb-w)]" style={themeVars(theme)}>
      <ShortcutSidebar allowed={allowed} />
      <header className="sticky top-0 z-30 border-b-2 bg-white" style={{ borderColor: theme.border }}>
        <div className="mod-bar h-1 w-full" />
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-4 py-3 sm:px-8">
          <div className="flex min-w-0 items-center gap-2.5">
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                  className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-300 text-zinc-700 transition-colors hover:border-zinc-950 hover:text-zinc-950 md:hidden"
                >
                  <Menu size={18} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="mobile-nav-sheet" className="w-[85vw] max-w-xs overflow-y-auto p-0">
                <SheetTitle className="border-b border-zinc-200 px-5 py-4 text-left font-head text-base font-extrabold tracking-tighter">
                  {profile?.company_name || "AGARWAL360"}
                </SheetTitle>
                <nav className="px-2 py-3">
                  {menus.map((m) =>
                    m.to ? (
                      <button
                        key={m.label}
                        data-testid={`m-${m.testId}`}
                        onClick={() => {
                          setNavOpen(false);
                          navigate(m.to);
                        }}
                        className="block w-full px-3 py-2.5 text-left text-sm font-bold uppercase tracking-[0.12em] transition-colors"
                        style={
                          pathname === m.to || (m.to === "/dashboard" && pathname === "/")
                            ? { background: MENU_COLORS[m.label], color: "#fff" }
                            : { color: MENU_COLORS[m.label] || "#27272a" }
                        }
                      >
                        {m.label}
                      </button>
                    ) : (
                      <div key={m.label} className="mt-1 border-b border-zinc-100 last:border-b-0">
                        <button
                          data-testid={`m-${m.testId}`}
                          onClick={() => setOpenGroup(openGroup === m.label ? null : m.label)}
                          className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-bold uppercase tracking-[0.12em] text-zinc-800 transition-colors hover:bg-zinc-100"
                        >
                          {m.label}
                          <ChevronDown
                            size={15}
                            className={`transition-transform duration-200 ${openGroup === m.label ? "rotate-180" : ""}`}
                          />
                        </button>
                        {openGroup === m.label && (
                          <div className="pb-2" data-testid={`m-group-${m.testId}`}>
                            {m.links.map((l) => (
                              <button
                                key={l.to || l.action}
                                data-testid={`m-${l.testId}`}
                                onClick={() => {
                                  setNavOpen(false);
                                  l.action === "company" ? setCompanyOpen(true) : navigate(l.to);
                                }}
                                className="block w-full border-l-2 py-2 pl-6 pr-3 text-left text-sm transition-colors"
                                style={{
                                  borderColor: pathname === l.to ? MENU_COLORS[m.label] : "#e4e4e7",
                                  background: pathname === l.to ? `${MENU_COLORS[m.label]}14` : "transparent",
                                  fontWeight: pathname === l.to ? 600 : 400,
                                }}
                              >
                                {l.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </nav>
              </SheetContent>
            </Sheet>
            {profile?.logo_path && (
              <FabricImage path={profile.logo_path} alt="Company logo" className="h-9 w-auto max-w-[110px] object-contain" w={200} />
            )}
            <span className="font-head truncate text-base font-extrabold tracking-tighter" data-testid="app-logo">
              {profile?.company_name || "AGARWAL360"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-zinc-500 lg:block">{user?.email}</span>
            <Button data-testid="logout-button" variant="outline" size="sm" className="rounded-sm" onClick={logout}>
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>

        <nav className="mx-auto hidden max-w-[1500px] px-4 sm:px-8 md:block">
          <div className="flex gap-1 pb-1">
            {menus.map((m) => {
              if (m.to) {
                const isActive = pathname === m.to || (m.to === "/dashboard" && pathname === "/");
                return (
                  <button
                    key={m.label}
                    data-testid={m.testId}
                    onClick={() => navigate(m.to)}
                    className="shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] outline-none transition-colors duration-200"
                    style={{
                      color: MENU_COLORS[m.label] || "#71717a",
                      borderColor: isActive ? MENU_COLORS[m.label] : "transparent",
                      background: isActive ? `${MENU_COLORS[m.label]}14` : "transparent",
                    }}
                  >
                    {m.label}
                  </button>
                );
              }
            const active = m.links.some((l) => l.to === pathname) ||
              (m.testId === "menu-admin" && pathname.startsWith("/masters"));
              return (
                <DropdownMenu key={m.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={m.testId}
                      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] outline-none transition-colors duration-200"
                      style={{
                        color: MENU_COLORS[m.label] || "#71717a",
                        borderColor: active ? MENU_COLORS[m.label] : "transparent",
                        background: active ? `${MENU_COLORS[m.label]}14` : "transparent",
                      }}
                    >
                      {m.label}
                      <ChevronDown size={13} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="rounded-sm">
                    {m.links.map((l) => (
                      <DropdownMenuItem
                        key={l.to || l.action}
                        data-testid={l.testId}
                        onSelect={() => (l.action === "company" ? setCompanyOpen(true) : navigate(l.to))}
                        className="cursor-pointer rounded-sm text-sm"
                      >
                        {l.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
        </nav>
      </header>
      <CompanyDialog open={companyOpen} onOpenChange={setCompanyOpen} onSaved={() => window.location.reload()} />
      <SalesRateDialog open={rateOpen} onOpenChange={setRateOpen} />
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent data-testid="shortcuts-dialog" className="max-h-[85vh] max-w-lg overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">Keyboard shortcuts</DialogTitle>
          </DialogHeader>
          {[
            ...SIDEBAR_GROUPS.map((g) => ({
              title: g.title,
              rows: g.items
                .filter((i) => (i.to || i.action) && (!i.perm || allowed.includes(i.perm)))
                .map((i) => [i.key, i.label]),
            })),
            { title: "Other", rows: EXTRA_SHORTCUTS.filter((i) => !i.perm || allowed.includes(i.perm)).map((i) => [i.key, i.label]) },
            { title: "Keys", rows: ACTION_KEYS },
          ]
            .filter((s) => s.rows.length)
            .map((s) => (
              <div key={s.title} className="mb-2">
                <p className="pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">{s.title}</p>
                <div className="divide-y divide-zinc-100">
                  {s.rows.map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between gap-4 py-1.5 text-sm">
                      <span className="text-zinc-700">{label}</span>
                      <kbd className="shrink-0 border border-zinc-300 bg-zinc-50 px-2 py-0.5 font-mono text-xs font-semibold">
                        {key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </DialogContent>
      </Dialog>
      {children}
      {allowed.includes("items") && pathname !== "/quick-add" && (
        <button
          data-testid="quick-add-fab"
          aria-label="Quick add photo"
          onClick={() => navigate("/quick-add")}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg shadow-black/25 transition-transform active:scale-95 md:hidden"
          style={{ background: theme.primary }}
        >
          <Camera size={22} />
        </button>
      )}
    </div>
  );
};
